import { supabase } from '../../lib/supabase';

/**
 * Intelligence Memory Engine
 * Computes and stores lightweight historical stats about user behavior
 * Used by focus intelligence pipelines for personalized insights
 */

/**
 * Compute all memory statistics for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Memory stats object
 */
async function computeUserMemory(userId) {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

  // 1. Content by channel (7d and 30d)
  const contentByChannel7d = {};
  const contentByChannel30d = {};

  // Query instagram_content
  const { data: instagramData } = await supabase
    .schema('public')
    .from('instagram_content')
    .select('channel, created_at')
    .eq('user_id', userId);

  if (instagramData) {
    instagramData.forEach((item) => {
      // Match logic from focusEngine: default to 'Instagram' if channel not set
      const channel = item.channel || 'Instagram';
      const createdAt = new Date(item.created_at);
      
      if (createdAt > sevenDaysAgo) {
        contentByChannel7d[channel] = (contentByChannel7d[channel] || 0) + 1;
      }
      if (createdAt > thirtyDaysAgo) {
        contentByChannel30d[channel] = (contentByChannel30d[channel] || 0) + 1;
      }
    });
  }

  // Query deepsearch.items
  const { data: deepsearchData } = await supabase
    .schema('deepsearch')
    .from('items')
    .select('meta, created_at')
    .eq('user_id', userId);

  if (deepsearchData) {
    deepsearchData.forEach((item) => {
      const channel = item.meta?.channel || null;
      if (!channel) return;
      const createdAt = new Date(item.created_at);
      
      if (createdAt > sevenDaysAgo) {
        contentByChannel7d[channel] = (contentByChannel7d[channel] || 0) + 1;
      }
      if (createdAt > thirtyDaysAgo) {
        contentByChannel30d[channel] = (contentByChannel30d[channel] || 0) + 1;
      }
    });
  }

  // 2. Chats started in last 7 days
  const { data: conversationsRecent } = await supabase
    .schema('public')
    .from('conversations')
    .select('id, created_at')
    .eq('user_id', userId)
    .gte('created_at', sevenDaysAgo.toISOString());

  const chatsStarted7d = conversationsRecent?.length || 0;

  // 3. Average message length in last 7 days
  const { data: messagesRecent } = await supabase
    .from('chat_messages')
    .select('content')
    .eq('user_id', userId)
    .eq('role', 'user')
    .gte('created_at', sevenDaysAgo.toISOString());

  let avgMessageLength7d = 0;
  if (messagesRecent && messagesRecent.length > 0) {
    const totalLength = messagesRecent.reduce((sum, msg) => sum + (msg.content?.length || 0), 0);
    avgMessageLength7d = Math.round(totalLength / messagesRecent.length);
  }

  // 4. Top words in last 7 days (from user messages)
  const topWords7d = [];
  if (messagesRecent && messagesRecent.length > 0) {
    const wordCounts = {};
    messagesRecent.forEach((msg) => {
      const words = (msg.content || '').toLowerCase().match(/\b[a-z]{3,}\b/g) || [];
      words.forEach((word) => {
        // Filter out common stop words
        const stopWords = ['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'she', 'use', 'her', 'many', 'than', 'them', 'this', 'that', 'with', 'have', 'from', 'they', 'know', 'want', 'been', 'good', 'much', 'some', 'time', 'very', 'when', 'come', 'here', 'just', 'like', 'long', 'make', 'many', 'over', 'such', 'take', 'than', 'them', 'well', 'were', 'what', 'your', 'about', 'after', 'again', 'before', 'could', 'every', 'first', 'found', 'great', 'might', 'never', 'other', 'place', 'right', 'should', 'still', 'think', 'those', 'under', 'while', 'where', 'which', 'would', 'world', 'being', 'during', 'having', 'little', 'often', 'seems', 'these', 'through', 'until', 'within', 'without'];
        if (!stopWords.includes(word) && word.length >= 3) {
          wordCounts[word] = (wordCounts[word] || 0) + 1;
        }
      });
    });

    // Get top 10 words
    const sortedWords = Object.entries(wordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([word]) => word);
    topWords7d.push(...sortedWords);
  }

  // 5. Abandoned threads (conversations not touched in 14+ days)
  const { data: allConversations } = await supabase
    .schema('public')
    .from('conversations')
    .select('id, updated_at')
    .eq('user_id', userId);

  const abandonedThreads = [];
  if (allConversations) {
    allConversations.forEach((conv) => {
      const updatedAt = new Date(conv.updated_at);
      if (updatedAt < fourteenDaysAgo) {
        abandonedThreads.push(conv.id);
      }
    });
  }

  // 6. Conversation switch rate
  // Count how many conversations user has vs how many they've actively used
  const { data: allMessages } = await supabase
    .from('chat_messages')
    .select('conversation_id, created_at')
    .eq('user_id', userId)
    .eq('role', 'user');

  const conversationIds = new Set();
  const conversationDates = {};
  
  if (allMessages) {
    allMessages.forEach((msg) => {
      conversationIds.add(msg.conversation_id);
      if (!conversationDates[msg.conversation_id]) {
        conversationDates[msg.conversation_id] = [];
      }
      conversationDates[msg.conversation_id].push(new Date(msg.created_at));
    });
  }

  // Calculate switch rate: if user has messages in many conversations but few messages per conversation
  const totalConversations = allConversations?.length || 0;
  const activeConversations = conversationIds.size;
  let conversationSwitchRate = 0;
  
  if (totalConversations > 0 && activeConversations > 0) {
    // Average messages per conversation
    const avgMessagesPerConv = allMessages ? allMessages.length / activeConversations : 0;
    // Switch rate is inverse of concentration (lower messages per conv = higher switch rate)
    conversationSwitchRate = Math.min(1, Math.max(0, 1 - (avgMessagesPerConv / 10)));
  }

  // 7. Upload concentration score (0-1, where 1 = all uploads in one channel)
  const totalUploads7d = Object.values(contentByChannel7d).reduce((sum, count) => sum + count, 0);
  let uploadConcentrationScore = 0;
  if (totalUploads7d > 0) {
    const maxChannelCount = Math.max(...Object.values(contentByChannel7d));
    uploadConcentrationScore = maxChannelCount / totalUploads7d;
  }

  // 8. Chat concentration score (0-1, where 1 = all chats in one folder/topic)
  // Get conversations with folder_id
  const { data: conversationsWithFolders } = await supabase
    .schema('public')
    .from('conversations')
    .select('id, folder_id')
    .eq('user_id', userId)
    .not('folder_id', 'is', null);

  const folderCounts = {};
  if (conversationsWithFolders) {
    conversationsWithFolders.forEach((conv) => {
      const folderId = conv.folder_id;
      folderCounts[folderId] = (folderCounts[folderId] || 0) + 1;
    });
  }

  const totalChatsInFolders = Object.values(folderCounts).reduce((sum, count) => sum + count, 0);
  let chatConcentrationScore = 0;
  if (totalChatsInFolders > 0) {
    const maxFolderCount = Math.max(...Object.values(folderCounts));
    chatConcentrationScore = maxFolderCount / totalChatsInFolders;
  }

  return {
    content_by_channel_7d: contentByChannel7d,
    content_by_channel_30d: contentByChannel30d,
    chats_started_7d: chatsStarted7d,
    avg_message_length_7d: avgMessageLength7d,
    top_words_7d: topWords7d,
    abandoned_threads: abandonedThreads,
    conversation_switch_rate: conversationSwitchRate,
    upload_concentration_score: uploadConcentrationScore,
    chat_concentration_score: chatConcentrationScore,
  };
}

/**
 * Load user memory from database
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Memory object or null if not found
 */
export async function loadUserMemory(userId) {
  try {
    const { data, error } = await supabase
      .schema('public')
      .from('intelligence_memory')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Not found - return null, will be computed on first run
        return null;
      }
      console.error('[MemoryEngine] Error loading memory:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('[MemoryEngine] Error loading memory:', error);
    return null;
  }
}

/**
 * Update or create user memory in database
 * @param {string} userId - User ID
 * @param {Object} memoryStats - Memory statistics object
 * @returns {Promise<Object|null>} Updated memory object or null on error
 */
export async function updateUserMemory(userId, memoryStats) {
  try {
    const memoryData = {
      user_id: userId,
      content_by_channel_7d: memoryStats.content_by_channel_7d || {},
      content_by_channel_30d: memoryStats.content_by_channel_30d || {},
      chats_started_7d: memoryStats.chats_started_7d || 0,
      avg_message_length_7d: memoryStats.avg_message_length_7d || 0,
      top_words_7d: memoryStats.top_words_7d || [],
      abandoned_threads: memoryStats.abandoned_threads || [],
      conversation_switch_rate: memoryStats.conversation_switch_rate || 0,
      upload_concentration_score: memoryStats.upload_concentration_score || 0,
      chat_concentration_score: memoryStats.chat_concentration_score || 0,
      computed_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .schema('public')
      .from('intelligence_memory')
      .upsert(memoryData, {
        onConflict: 'user_id',
      })
      .select()
      .single();

    if (error) {
      console.error('[MemoryEngine] Error updating memory:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('[MemoryEngine] Error updating memory:', error);
    return null;
  }
}

/**
 * Compute and update user memory
 * This is the main function to call when memory needs to be refreshed
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Updated memory object
 */
export async function refreshUserMemory(userId) {
  try {
    console.log('[MemoryEngine] Computing memory for user', userId);
    const memoryStats = await computeUserMemory(userId);
    const updated = await updateUserMemory(userId, memoryStats);
    console.log('[MemoryEngine] Memory updated:', updated ? 'success' : 'failed');
    return updated;
  } catch (error) {
    console.error('[MemoryEngine] Error refreshing memory:', error);
    return null;
  }
}

